import React, { useEffect, useMemo, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { BookOpen, Search } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Input } from '@/components/ui/input';
import { OdrLoadingSpinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

const LIST_KNOWLEDGE_BASE_ARTICLES = gql`
  query ListKnowledgeBaseArticles {
    listKnowledgeBaseArticles {
      id
      slug
      title
    }
  }
`;

const GET_KNOWLEDGE_BASE_ARTICLE = gql`
  query GetKnowledgeBaseArticle($slug: String!) {
    getKnowledgeBaseArticle(slug: $slug) {
      id
      slug
      title
      content
      contentFormat
    }
  }
`;

type ArticleListItem = {
  id: number;
  slug: string;
  title: string;
};

export const KnowledgeBasePage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [search, setSearch] = useState('');
  const { data, loading, error } = useQuery(LIST_KNOWLEDGE_BASE_ARTICLES);
  const articles = useMemo<ArticleListItem[]>(() => data?.listKnowledgeBaseArticles || [], [data]);
  const selectedSlug = slug || articles[0]?.slug || '';
  const articleQuery = useQuery(GET_KNOWLEDGE_BASE_ARTICLE, {
    variables: { slug: selectedSlug },
    skip: !selectedSlug,
  });

  useEffect(() => {
    if (!slug && articles[0]?.slug) {
      navigate(`/support/knowledge-base/${articles[0].slug}`, { replace: true });
    }
  }, [articles, navigate, slug]);

  const filteredArticles = articles.filter((article) =>
    article.title.toLowerCase().includes(search.trim().toLowerCase())
  );
  const article = articleQuery.data?.getKnowledgeBaseArticle;
  const renderedContent = article?.contentFormat === 'markdown'
    ? marked.parse(article?.content || '', { async: false })
    : article?.content || '';
  const sanitizedContent = DOMPurify.sanitize(`${renderedContent || ''}`);

  if (loading) {
    return <OdrLoadingSpinner />;
  }

  if (error) {
    return <div className="p-6 text-sm text-[var(--semantic-error)]">Unable to load the knowledge base: {error.message}</div>;
  }

  return (
    <div className="min-h-screen bg-[var(--neutral-gray-10)] px-4 py-5 sm:px-6">
      <div className="mx-auto max-w-[1440px]">
        <div className="mb-4 flex items-center gap-3">
          <BookOpen className="h-6 w-6 text-[var(--azure-primary)]" />
          <div>
            <h1 className="text-2xl font-semibold text-[var(--neutral-gray-120)]">Knowledge Base</h1>
            <p className="text-sm text-[var(--neutral-gray-90)]">Support procedures and troubleshooting references</p>
          </div>
        </div>

        <div className="grid min-h-[680px] overflow-hidden rounded-md border border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] shadow-[var(--shadow-light)] lg:grid-cols-[320px_minmax(0,1fr)]">
          <aside className="border-b border-[var(--neutral-gray-30)] lg:border-b-0 lg:border-r">
            <div className="border-b border-[var(--neutral-gray-30)] p-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-2 h-4 w-4 text-[var(--neutral-gray-80)]" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search articles"
                  className="h-8 pl-8"
                />
              </div>
            </div>
            <nav aria-label="Knowledge base articles" className="max-h-[620px] overflow-y-auto p-2">
              {filteredArticles.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => navigate(`/support/knowledge-base/${item.slug}`)}
                  className={cn(
                    'mb-1 w-full rounded-sm px-3 py-2 text-left text-sm transition-colors',
                    item.slug === selectedSlug
                      ? 'bg-[var(--surface-muted)] font-medium text-[var(--azure-primary)]'
                      : 'text-[var(--neutral-gray-110)] hover:bg-[var(--surface-muted)]'
                  )}
                >
                  {item.title}
                </button>
              ))}
              {filteredArticles.length === 0 ? (
                <p className="px-3 py-4 text-sm text-[var(--neutral-gray-80)]">No matching articles.</p>
              ) : null}
            </nav>
          </aside>

          <main className="min-w-0 p-5 sm:p-7">
            {articleQuery.loading ? <OdrLoadingSpinner /> : null}
            {articleQuery.error ? (
              <p className="text-sm text-[var(--semantic-error)]">Unable to load this article: {articleQuery.error.message}</p>
            ) : null}
            {article ? (
              <article>
                <h2 className="mb-5 text-xl font-semibold text-[var(--neutral-gray-120)]">{article.title}</h2>
                <div
                  className="prose max-w-none text-[var(--neutral-gray-100)] prose-headings:font-semibold prose-headings:text-[var(--neutral-gray-120)] prose-a:text-[var(--azure-primary)]"
                  dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                />
              </article>
            ) : null}
          </main>
        </div>
      </div>
    </div>
  );
};
